<script lang="ts" setup>
	/**
	 * Menu latéral.
	 *
	 * Chaque entrée est rendue conditionnellement à l'accessibilité de sa route :
	 * `useRouteGranted` lit les droits déclarés dans `meta.granted`, ce qui évite
	 * de dupliquer ici la liste des droits de chaque section.
	 */
	import { computed } from 'vue';
	import { useRouter } from 'vue-router';
	import { useRouteGranted, useUserStore } from '@common/index';

	const drawer = defineModel<boolean>({ default: true });

	const router = useRouter();
	const routeGranted = useRouteGranted();
	const userStore = useUserStore();

	const ENTRIES = [
		{ name: 'home', icon: 'mdi-view-dashboard', label: 'Tableau de bord' },
		{ name: 'live-map', icon: 'mdi-map-marker-radius', label: 'Carte en direct' },
		{ name: 'live-timeline', icon: 'mdi-timeline-clock', label: 'En direct' },
		{ name: 'rides', icon: 'mdi-car-clock', label: 'Courses' },
		{ name: 'drivers', icon: 'mdi-account-tie', label: 'Chauffeurs' },
		{ name: 'kiosks', icon: 'mdi-monitor-dashboard', label: 'Bornes' },
		{ name: 'places', icon: 'mdi-map-marker-multiple', label: 'Lieux' },
		{ name: 'users', icon: 'mdi-account-group', label: 'Utilisateurs' },
		{ name: 'rights', icon: 'mdi-shield-key', label: 'Rôles et droits' },
		{ name: 'settings', icon: 'mdi-tune', label: 'Paramètres' },
	];

	const visibleEntries = computed(() => ENTRIES.filter((entry) => routeGranted(entry.name)));

	const logout = async (): Promise<void> => {
		await userStore.logout();
		void router.replace({ name: 'login' });
	};
</script>

<template>
	<v-navigation-drawer v-model="drawer" width="252">
		<div class="pa-4 d-flex align-center ga-2">
			<v-icon color="primary" size="28">mdi-taxi</v-icon>
			<div>
				<div class="font-weight-bold">Monaco Taxi</div>
				<div class="text-caption text-medium-emphasis">Back-office</div>
			</div>
		</div>

		<v-divider />

		<v-list nav density="comfortable">
			<v-list-item
				v-for="entry of visibleEntries"
				:key="entry.name"
				:prepend-icon="entry.icon"
				:title="entry.label"
				:to="{ name: entry.name }"
			/>
		</v-list>

		<template #append>
			<v-divider />
			<div class="pa-3">
				<div class="text-body-2 font-weight-medium text-truncate">
					{{ userStore.displayName }}
				</div>
				<div class="text-caption text-medium-emphasis text-truncate mb-2">
					{{ userStore.me?.email }}
				</div>
				<v-btn block variant="tonal" prepend-icon="mdi-logout" @click="logout()">
					Se déconnecter
				</v-btn>
			</div>
		</template>
	</v-navigation-drawer>
</template>
